import React, { useCallback, useContext, useEffect, useState, useRef, useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from "@mui/material";
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle } from "react-resizable-panels";
import {
  AddCircleOutlineRounded,
  AddToDrive,
  ArrowDropDown,
  Check,
  Close,
  LabelOutlined,
  Link as LinkIcon,
  PhotoLibrary,
  Print,
  SaveAlt,
  Share,
} from "@mui/icons-material";

import {
  useAlbums,
  useAttachments,
  useIsMobile,
  useSelectedOrganization,
  useSnackbar,
  useTranslations,
  useUser,
} from "hooks";

import { Comment, History } from '@mui/icons-material';
import DialogHeader from "./DialogHeader";
import TaskShareLinkDialog from "./TaskShareLinkDialog";
import { getTranslation } from "common";
import { MAX_ATTACHMENTS } from "common/attachmentLimits";
import { buildFullTaskSnapshot } from "common/taskSnapshot";
import CommentsSection, {
  Comment as TSXComment,
  CompleteComment,
} from "components/CommentsSection";
import { flownodesApi, organizationsApi, processesApi, statisticsApi } from "api";
import taskShareApi from "api/taskShareApi";
import {
  buildTaskDetailDialogPreviewData,
  getFileExtension,
  getLabelDataFromJsonTags,
  getUserData,
  taskDetailDialogExport,
  toBase64,
} from "common/utilities";
import AttachmentList from "components/AttachmentList";
import AlbumGallery from "components/AlbumGallery";
import AlbumEditDialog, { AlbumCandidate } from "components/dialogs/AlbumEditDialog";
import { Album } from "model/albumModel";
import { Priority } from "hooks/usePriorities";
import { SequenceFlow, Task } from "model/activitiesModel";
import LabelManager from "../labels/LabelManager";
import FieldsList, { type FieldsListHandle } from "components/FieldsList";
import { runAfterFieldsSaved } from "components/custom-fields/runtimeDraft";
import UserDataFieldsInfo from "components/UserDataFieldsInfo";
import Labels from "components/labels/Labels";
import { UserData, UserDataUpdate } from "hooks/useUserDataKeys";
import useSubscriptionFeatures from "hooks/useSubscriptionFeatures";
import { useNavigate } from "react-router-dom";
import HistoriesSection, { HistoryEntry } from "components/HistoriesSection";
import GoogleDriveMenu from "components/menus/GoogleDriveMenu";
import ProcessNavigationMenu from "components/menus/ProcessNavigationMenu";
import DropZone from "components/DropZone";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";
import MobileTaskDetailDrawer from "components/myactivities-mobile/MobileTaskDetailDrawer";
import { RichTextEditor } from "components/Editor/plugins/RichTextEditor";
import { extractMentionedUserIds } from "components/Editor/plugins/mentionUtils";
import type { MDXEditorMethods } from "@mdxeditor/editor";
import DOMPurify from "dompurify";
import ConfirmDialog from "./ConfirmDialog";
import {
  referencedAttachmentIds,
  type AttachmentHost,
} from "components/custom-fields/attachmentField";
import { ModalCoordinatorContext } from "contexts";
// Dettaglio SLA (F3c): originale/vigente/override nel dettaglio **esistente**
// della pagina attivita' (piano sez. 3 riga "Attivita'", sez. 5 F3c). Fuori
// dalla pagina attivita' non rende nulla: nessun dialog nuovo.
import SlaTaskSlaDetail from "components/sla/SlaTaskSlaDetail";


interface BaseTaskDetailDialogProps {
  open: boolean;
  doing: boolean;
  onClose: () => void;
  nextButtonLabel?: string;
  undoButtonLabel?: string;
  handleNextStep?: (task: Task) => void;
  handleBackStep?: (task: Task) => void;
  handleSequenceFlowChoice?: (task: Task, sequenceFlow: SequenceFlow) => void;
  administrator?: boolean;
  task?: Task;
  readonly?: boolean;
  historyMode?: boolean;
  onPriorityUpdate?: (taskId: number, newPriority: Priority) => void;
  refresh?: () => void;
}

const TaskDetailDialog: React.FC<BaseTaskDetailDialogProps> = ({
  open,
  doing,
  onClose,
  task,
  readonly = false,
  handleSequenceFlowChoice,
  handleNextStep,
  handleBackStep,
  nextButtonLabel,
  undoButtonLabel,
  administrator,
  onPriorityUpdate,
  refresh,
  historyMode = false,
}) => {
  // hooks & state
  const user = useUser();
  const descriptionDetailRef = useRef<MDXEditorMethods>(null);
  const fieldsRef = useRef<FieldsListHandle>(null);
  const transitioning = useRef(false);
  const pasteCaptureRef = useRef<HTMLInputElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [shareLinkOpen, setShareLinkOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    storageDiskCanUse,
    bypassSubscription,
    checkLoading,
    refreshSubscriptionFeatures,
    storageUsage,
  } = useSubscriptionFeatures({ enabled: open });
  const navigate = useNavigate();
  const menuOpen = Boolean(anchorEl);

  // snackbar, translations, organization
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();
  const afterFieldsSaved = async (action: () => void | Promise<void>): Promise<boolean> => {
    if (transitioning.current) return false;
    transitioning.current = true;
    try {
      const completed = await runAfterFieldsSaved(
        () => fieldsRef.current?.flush() ?? Promise.resolve(false),
        action
      );
      if (!completed) {
        sendSnack({ type: "error", message: getTranslation(translations, "generic.error.caption") || "Impossibile salvare i campi" });
        return false;
      }
      return true;
    } catch {
      sendSnack({ type: "error", message: getTranslation(translations, "generic.error.caption") || "Impossibile completare l'azione" });
      return false;
    } finally {
      transitioning.current = false;
    }
  };

  /**
   * Salva gli userdata del task aperto.
   *
   * Task, contesto e revisioni provengono dalla stessa sessione della lista,
   * dopo il flush dei valori: Delphi non deve scegliere un contesto diverso.
   */
  const saveUserdata = async (
    payload: { [key: string]: UserDataUpdate },
    operationId?: string,
    removedFieldIds?: readonly string[],
    context?: import("model/CustomFields").RuntimeContext
  ): Promise<void> => {
    const { data } = await processesApi.put(`/${task?.idprocess}/userdata`, {
      operationId,
      adHocUserdata: payload,
      removedFieldIds,
      contextId: context?.contextId,
      expectedRevision: context?.revision,
      taskId: task?.idprocesstoken == null ? undefined : String(task.idprocesstoken),
    });
    sendSnack({ message: data.responsemessage, type: "success" });
    refresh && refresh();
  };
  const isMobile = useIsMobile();
  const selectedOrganization = useSelectedOrganization();

  // Register with the global ModalCoordinator when the dialog is open, so
  // that the AI drawer can dismiss us automatically (the AI fab sits above
  // every backdrop and would otherwise let the user open the AI while we
  // are still on screen, leaving the AI hidden behind us).
  const { actions: modalCoordinatorActions } = useContext(ModalCoordinatorContext);
  useEffect(() => {
    if (!open) return;
    return modalCoordinatorActions.registerModal({
      id: "taskDetailDialog",
      onClose,
    });
  }, [open, onClose, modalCoordinatorActions]);

  // Persist panel layout in localStorage
  const LAYOUT_KEY = "taskdetail-layout";
  const loadLayout = (key: string): { [id: string]: number } | undefined => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : undefined; } catch { return undefined; }
  };
  const saveLayout = (key: string, layout: { [id: string]: number }) => {
    try { localStorage.setItem(key, JSON.stringify(layout)); } catch { }
  };
  let organizationId: number | null | undefined = null;
  if (selectedOrganization) {
    organizationId = selectedOrganization.id;
  }

  // data state
  const [comments, setComments] = useState<CompleteComment[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [attachments, , getAttachments, images, histories, loadingAttachments] = useAttachments(task || null);
  // Immagini-allegato selezionabili in un album: le stesse anteprime già caricate.
  // Un album è fatto solo di allegati-immagine, quindi senza immagini un album non
  // può esistere: in quel caso NON si chiama nemmeno il backend.
  const albumCandidates: AlbumCandidate[] = useMemo(() => {
    const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
    return attachments
      .filter((a) =>
        IMAGE_EXTENSIONS.includes((getFileExtension(a.filename) || "").toLowerCase())
      )
      .map((a) => ({ id: a.id, filename: a.filename, thumb: images[a.id] }));
  }, [attachments, images]);
  const [albums, getAlbums] = useAlbums(task || null, albumCandidates.length > 0);
  const [albumEditor, setAlbumEditor] = useState<{ open: boolean; album: Album | null }>({
    open: false,
    album: null,
  });
  const [uploadAttachmentError, setUploadAttachmentError] = useState(false);
  const [maxAttachmentsError, setMaxAttachmentsError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [allPriorities, setAllPriorities] = useState<Priority[]>([]);
  const [selectedPriority, setSelectedPriority] = useState<Priority | null>(null);
  const [isLabelManagerShown, setIsLabelManagerShown] = useState<boolean>(false);
  const [userdataEntries, setUserDataEntries] = useState<{ [key: string]: UserData }>({});
  const [fieldAttachmentIds, setFieldAttachmentIds] = useState<readonly string[]>([]);
  /** Apre il dialog "aggiungi campo" di FieldsList: i campi ad-hoc del processo. */
  const [openAddFieldDialog, setOpenAddFieldDialog] = useState(false);
  const [activeView, setActiveView] = useState<'histories' | 'comments'>('comments');
  const [descriptionDetail, setDescriptionDetail] = useState<string | null>(null);

  // Inline editing state
  const [editingProcessName, setEditingProcessName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedProcessName, setEditedProcessName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [isDescriptionDetailFocused, setIsDescriptionDetailFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [fullContentDialog, setFullContentDialog] = useState({
    open: false,
    title: "",
    content: "",
  });
  // Confirmation gate for editing the description detail of an already closed (history) task
  const [editingConfirmed, setEditingConfirmed] = useState(false);
  const [showEditClosedConfirm, setShowEditClosedConfirm] = useState(false);

  // Determine if user can edit process details (admin or process starter)
  const isProcessStarter = useMemo(() => {
    if (!user || !history || history.length === 0) return false;
    const startEntry = history[0];
    const owner = startEntry?.owner?.trim();
    if (!owner) return false;
    const userFullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
    return owner === userFullName || owner === user.email || owner === user.username;
  }, [user, history]);

  const canEditProcessDetails = administrator || isProcessStarter;

  // When opening a closed (history) task as admin/starter, the description detail
  // editor stays readOnly until the user explicitly confirms they want to edit it.
  const isHistoryReadOnlyGate =
    historyMode && canEditProcessDetails && !editingConfirmed;

  const descriptionDetailReadOnly = !canEditProcessDetails || isHistoryReadOnlyGate;

  // Reset state when task changes to avoid showing stale data from previous card
  useEffect(() => {
    setComments([]);
    setHistory([]);
    setShowMore(false);
    setUserDataEntries({});
    setFieldAttachmentIds([]);
    setSelectedPriority(null);
    setActiveView('comments');
    setDescriptionDetail(null);
    setEditingProcessName(false);
    setEditingDescription(false);
    setEditingConfirmed(false);
    setShowEditClosedConfirm(false);
  }, [task?.idprocesstoken]);

  // helper toggles
  const toggleShowMore = () => setShowMore((c) => !c);
  const handleLabelManagerClose = () => setIsLabelManagerShown(false);
  const closeFullContentDialog = () => {
    setFullContentDialog({ open: false, title: "", content: "" });
  };

  // Inline editing handlers
  const startEditProcessName = () => {
    setEditedProcessName(task?.processmodelname || "");
    setEditingProcessName(true);
  };
  const startEditDescription = () => {
    setEditedDescription(task?.description || "");
    setEditingDescription(true);
  };
  const cancelEditProcessName = () => setEditingProcessName(false);
  const cancelEditDescription = () => setEditingDescription(false);

  const closeEditClosedConfirm = () => setShowEditClosedConfirm(false);
  const confirmEditClosed = () => {
    setEditingConfirmed(true);
    setShowEditClosedConfirm(false);
  };

  const saveProcessName = async () => {
    if (!task || !editedProcessName.trim()) return;
    setSaving(true);
    try {
      await processesApi.put(`/${task.idprocess}`, { processmodelname: editedProcessName.trim() });
      task.processmodelname = editedProcessName.trim();
      setEditingProcessName(false);
      sendSnack({ type: "success", message: getTranslation(translations, "pm.button.save") });
      refresh?.();
    } catch {
      // sendSnack({ type: "error", message: getTranslation(translations, "generic.error") || "Error" });
    } finally {
      setSaving(false);
    }
  };

  const saveDescription = async () => {
    if (!task) return;
    setSaving(true);
    try {
      await processesApi.put(`/${task.idprocess}`, { description: editedDescription.trim() });
      task.description = editedDescription.trim();
      setEditingDescription(false);
      sendSnack({ type: "success", message: getTranslation(translations, "pm.button.save") });
      refresh?.();
    } catch {
      // sendSnack({ type: "error", message: getTranslation(translations, "generic.error") || "Error" });
    } finally {
      setSaving(false);
    }
  };

  const saveDescriptionDetail = async () => {
    if (!task) return;
    const markdown = descriptionDetailRef.current?.getMarkdown();
    if (markdown === undefined) return;
    try {
      await processesApi.put(`/${task.idprocess}`, { descriptiondetail: markdown });
      setDescriptionDetail(markdown);
    } catch {
      // ignore
    }
  };

  const buildAttachmentPreviews = () => {
    if (!task) return [];
    return attachments
      .map((attachment) => {
        const extension = getFileExtension(attachment.filename);
        const image = images[attachment.id];
        if (!extension || !image) {
          return null;
        }

        const mimeTypeByExtension: Record<string, string> = {
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
          webp: "image/webp",
        };
        const mimeType = mimeTypeByExtension[extension];
        if (!mimeType) {
          return null;
        }

        return {
          name: attachment.filename,
          dataUrl: image,
          mimeType,
        };
      })
      .filter((preview): preview is { name: string; dataUrl: string; mimeType: string } => preview !== null);
  };

  const getTaskForExport = () =>
    task ? { ...task, descriptiondetail: descriptionDetail ?? task.descriptiondetail } : null;

  const handleDownloadTaskDocument = async () => {
    const exportTask = getTaskForExport();
    if (!exportTask) return;

    setDownloadingDocument(true);
    try {
      taskDetailDialogExport({
        task: exportTask,
        selectedPriorityName: selectedPriority?.name,
        userdataEntries,
        yesLabel: getTranslation(translations, "generic.yes.caption"),
        attachmentPreviews: buildAttachmentPreviews(),
        organizationName: selectedOrganization?.name || "",
      });
    } catch {
    } finally {
      setDownloadingDocument(false);
    }
  };

  const handlePreviewTaskDocument = () => {
    const exportTask = getTaskForExport();
    if (!exportTask) return;
    try {
      const blob = taskDetailDialogExport({
        task: exportTask,
        selectedPriorityName: selectedPriority?.name,
        userdataEntries,
        yesLabel: getTranslation(translations, "generic.yes.caption"),
        attachmentPreviews: buildAttachmentPreviews(),
        organizationName: selectedOrganization?.name || "",
        mode: "blob",
      });
      if (blob) {
        const url = URL.createObjectURL(blob);
        setPreviewPdfUrl(url);
        setPreviewOpen(true);
      }
    } catch {
      // sendSnack({
      //   type: "error",
      //   message: getTranslation(translations, "generic.error") || "Error",
      // });
    }
  };

  const handleShareTaskDocument = async () => {
    const exportTask = getTaskForExport();
    if (!exportTask) return;

    try {
      const blob = taskDetailDialogExport({
        task: exportTask,
        selectedPriorityName: selectedPriority?.name,
        userdataEntries,
        yesLabel: getTranslation(translations, "generic.yes.caption"),
        attachmentPreviews: buildAttachmentPreviews(),
        organizationName: selectedOrganization?.name || "",
        mode: "blob",
      });

      if (!blob) return;

      const fileName = `${exportTask.flownodename || exportTask.name || "task-detail"}.pdf`.replace(/[<>:"/\\|?*]+/g, "-");
      const file = new File([blob], fileName, { type: "application/pdf" });
      if (!navigator.canShare?.({ files: [file] })) {
        sendSnack({
          type: "info",
          message: getTranslation(translations, "attachment.preview.label.not.available") || "Condivisione non supportata da questo browser.",
        });
        return;
      }

      await navigator.share({
        title: exportTask.flownodename || exportTask.name || "Documento task",
        files: [file],
      });
    } catch {
      // sendSnack({
      //   type: "error",
      //   message: getTranslation(translations, "generic.error") || "Error",
      // });
    }
  };

  const closePreview = () => {
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    setPreviewOpen(false);
  };

  const getMobilePreviewImageSrc = (dataUrl: string, mimeType: string) =>
    dataUrl.startsWith("data:") ? dataUrl : `data:${mimeType};base64,${dataUrl}`;

  const renderMobileTaskDocumentPreview = () => {
    const exportTask = getTaskForExport();
    if (!exportTask) return null;

    const previewData = buildTaskDetailDialogPreviewData({
      task: exportTask,
      selectedPriorityName: selectedPriority?.name,
      userdataEntries,
      yesLabel: getTranslation(translations, "generic.yes.caption"),
      attachmentPreviews: buildAttachmentPreviews(),
      organizationName: selectedOrganization?.name || "",
    });

    const renderRows = (rows: { label: string; value: string }[]) => (
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderBottom: 0,
          bgcolor: "background.paper",
        }}
      >
        {rows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: "grid",
              gridTemplateColumns: "38% 62%",
              borderBottom: 1,
              borderColor: "divider",
              minHeight: 42,
            }}
          >
            <Box sx={{ p: 1, borderRight: 1, borderColor: "divider", bgcolor: "action.hover" }}>
              <Typography variant="caption" fontWeight={700}>
                {row.label}
              </Typography>
            </Box>
            <Box sx={{ p: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {row.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );

    return (
      <Box sx={{ p: 2, bgcolor: "background.default", minHeight: "100%", overflow: "auto" }}>
        <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: "divider" }}>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              {selectedOrganization?.name || "Flowenti"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date().toLocaleString()}
            </Typography>
          </Stack>

          {previewData.documentRows.length > 0 && (
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Dati documento
              </Typography>
              {renderRows(previewData.documentRows)}
            </Stack>
          )}

          {previewData.userdataRows.length > 0 && (
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Dati personalizzati
              </Typography>
              {renderRows(previewData.userdataRows)}
            </Stack>
          )}

          {previewData.attachmentPreviews.length > 0 && (
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                Allegati
              </Typography>
              {previewData.attachmentPreviews.map((preview) => (
                <Box
                  key={preview.name}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    p: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                    {preview.name}
                  </Typography>
                  <Box
                    component="img"
                    src={getMobilePreviewImageSrc(preview.dataUrl, preview.mimeType)}
                    alt={preview.name}
                    sx={{ width: "100%", maxHeight: 360, objectFit: "contain", display: "block" }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    );
  };

  // Menu handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // fetch comments/history
  const getComments = useCallback(async () => {
    if (!task) return;
    try {
      const response = await processesApi.get(`${task.idprocess}/comments`);
      setComments([...response.data.comments]);
    } catch {
      // ignore
    }
  }, [task]);

  const getHistory = useCallback(async () => {
    if (!task) return;
    try {
      const response = await statisticsApi.get(`/process/${task.idprocesstoken}/tasks/history`);
      setHistory(response.data.data);
    } catch {
      // ignore
    }
  }, [task]);

  /**
   * Se il commento menziona qualcuno, congela uno snapshot COMPLETO della task
   * intestato ai menzionati: la notifica che riceveranno punterà a quello
   * (`kind='mention'`). Va scritto PRIMA della POST del commento, così la riga
   * esiste già quando il worker Delphi crea la notifica; il commento stesso
   * però non è ancora sul server, quindi si aggiunge qui: senza, il
   * menzionato aprirebbe lo snapshot "un momento prima" e non leggerebbe la
   * menzione. Best-effort: se fallisce, il commento parte comunque e la
   * notifica ricade sul link interno.
   */
  const createMentionSnapshot = async (comment: string) => {
    const mentionIds = extractMentionedUserIds(comment);
    if (!task || !selectedOrganization?.id || mentionIds.length === 0) return;
    // Niente `idflownode`: i commenti del server non lo portano, quindi
    // `CommentsSection` li raggruppa tutti su "unknown::activityname". Con un
    // id numerico il commento locale finirebbe in un gruppo a parte.
    const pendingComment = {
      id: -1,
      idcomment: -1,
      activityname:
        task.flownodename ?? comments.find((c) => c.activityname)?.activityname ?? "",
      username: "",
      comment,
      creation: new Date().toISOString(),
      can_edit: false,
      edited: false,
      user: { id: user?.userId, firstname: user?.firstname, lastname: user?.lastname },
    } as CompleteComment;
    try {
      await taskShareApi.post(`/${task.idprocesstoken}/share/mention`, {
        idorganization: selectedOrganization.id,
        mention_user_ids: mentionIds,
        fields: buildFullTaskSnapshot(task, {
          attachments,
          comments: [...comments, pendingComment],
          history,
          albums,
        }),
      });
    } catch (e) {
      // La menzione non deve mai impedire l'invio del commento, ma un
      // fallimento silenzioso renderebbe illeggibile la notifica senza che
      // nessuno se ne accorga: lo si lascia almeno nei log.
      console.error("[mention] snapshot non creato", e);
    }
  };

  const onComment = async ({ comment }: TSXComment) => {
    if (task && comment) {
      await createMentionSnapshot(comment);
      try {
        await flownodesApi.post(`${task.idprocesstoken}/comments`, { comment });
        getComments();
        // Il contatore dei commenti vive sulla riga della card, che arriva dal
        // server: senza una rilettura resta il numero di prima.
        refresh?.();
      } catch {
        // ignore
      }
    }
  };

  const onEditComment = async (id: number, comment: string) => {
    if (task && comment) {
      try {
        await processesApi.put(`${task.idprocess}/comments/${id}`, { comment });
        getComments();
      } catch {
      }
    }
  };

  // upload local files
  const handleUploadFile = async (files: FileList | File[] | null) => {
    if (!task) return;
    setUploading(true);
    let promises: Promise<any>[] = [];
    let hasError = false;
    try {
      if (files && files.length > 0) {
        const newFiles = Array.from(files);
        const fileSize = newFiles.reduce((acc, file) => acc + file.size, 0);
        const totalSize = (storageUsage?.using || 0) + fileSize;
        if (totalSize > (storageUsage?.permit || 0) && !bypassSubscription) {
          setUploadAttachmentError(true);
          return;
        }
        const totalAttachmentCount = attachments.length + newFiles.length;
        // Il tetto non è più un numero scritto qui: lo legge anche il campo
        // allegato del modello, e due copie divergono.
        if (totalAttachmentCount > MAX_ATTACHMENTS) {
          setMaxAttachmentsError(true);
          return;
        }
        for (let file of newFiles) {
          // Cross-session check: check if filename is already in the server-side attachments
          if (attachments.some(a => a.filename === file.name)) {
            sendSnack({
              message: `${getTranslation(translations, "attachment.already.present") || "File già presente"}: ${file.name}`,
              type: "info",
            });
            continue;
          }

          const payload = {


            idorganization: organizationId,
            filename: file.name,
            cloudlink: "",
            idtype: 1,
            description: file.name,
            blob: await toBase64(file),
            size: file.size,
          };
          promises.push(flownodesApi.post(`${task.idprocesstoken}/attachments`, payload));
        }
        try {
          await Promise.all(promises);
        } catch {
          hasError = true;
        }
        if (!hasError && promises.length > 0) {
          sendSnack({
            type: "success",
            message: getTranslation(translations, "generic.fileuploadedsuccessfully"),
          });
        }
        refreshSubscriptionFeatures();
        getAttachments();
        // Aggiorna anche il contatore allegati della card (arriva dal server).
        refresh?.();
      }
      setUploadAttachmentError(false);
      setMaxAttachmentsError(false);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadFileRef = useRef(handleUploadFile);
  handleUploadFileRef.current = handleUploadFile;

  /**
   * Come un campo allegato carica i suoi file.
   *
   * Stessa rotta e **stessi limiti** del caricamento da questa dialog: le regole
   * stanno in `attachmentVerdict`, e qui resta l'esecuzione. Gli id si
   * risolvono **per nome** dopo il ricaricamento, perché la rotta risponde con
   * un messaggio e non con l'allegato creato — e il nome è già l'identità che
   * l'app usa per la deduplica.
   */
  const fieldAttachments: AttachmentHost = {
    onFieldAttachmentIdsChange: setFieldAttachmentIds,
    // I record interi: la riga del campo li mostra con gli stessi comandi
    // dell'elenco allegati (anteprima, rinomina, elimina, scarica).
    available: attachments,
    loading: loadingAttachments,
    refresh: getAttachments,
    // La storia — le versioni precedenti di ogni file — è la stessa mappa che
    // alimenta l'elenco "di contorno": `useAttachments` la carica per tutti gli
    // allegati del processo, quindi anche per quelli che un campo nomina.
    histories,
    budget: {
      existing: attachments.length,
      using: storageUsage?.using || 0,
      permit: storageUsage?.permit || 0,
      bypass: Boolean(bypassSubscription),
    },
    upload: async (files) => {
      if (!task) return [];
      for (const file of files) {
        await flownodesApi.post(`${task.idprocesstoken}/attachments`, {
          idorganization: organizationId,
          filename: file.name,
          cloudlink: "",
          idtype: 1,
          description: file.name,
          blob: await toBase64(file),
          size: file.size,
        });
      }
      const { data } = await processesApi.get(
        `${task.idprocess}/attachments/light`
      );
      const elenco: { id: number; filename: string }[] = data.attachments ?? [];
      getAttachments();
      refreshSubscriptionFeatures();
      // Vale anche per gli allegati caricati da un campo: il contatore della
      // card è uno solo.
      refresh?.();
      return files
        .map((file) => elenco.find((a) => a.filename === file.name)?.id)
        .filter((id): id is number => typeof id === "number");
    },
  };

  /**
   * Gli allegati "di contorno" — tutti quelli che nessun campo allegato nomina.
   *
   * Gli id arrivano dal contesto dei campi e dallo userdata legacy:
   * un file riferito da un campo si gestisce dalla riga di quel campo, e in due
   * elenchi sembrerebbe che l'uno sia la copia dell'altro. Il conto dei file
   * del task (tetto e dropzone) resta quello intero: il tetto è del processo,
   * non di questo elenco.
   */
  const surroundingAttachments = useMemo(() => {
    const referenced = referencedAttachmentIds(userdataEntries);
    return attachments.filter((attachment) =>
      !referenced.has(String(attachment.id)) && !fieldAttachmentIds.includes(String(attachment.id))
    );
  }, [attachments, userdataEntries, fieldAttachmentIds]);

  // priorities
  const fetchOrganizationPriorities = useCallback(async () => {
    if (!organizationId) return;
    try {
      const response = await organizationsApi.get(`/${organizationId}/priorities`);
      if (response && response.data && response.data.priorities) {
        setAllPriorities(response.data.priorities);
      }
    } catch {
      // ignore
    }
  }, [organizationId]);

  useEffect(() => {
    if (open) {
      fetchOrganizationPriorities();
    }
  }, [fetchOrganizationPriorities, open]);

  useEffect(() => {
    if (task && task.priority_name) {
      const priority = allPriorities.find((p) => p.name === task.priority_name);
      setSelectedPriority(priority || null);
    }
  }, [allPriorities, task]);

  const updatePriority = async (newPriority: Priority) => {
    if (!task) return;
    try {
      const { data } = await flownodesApi.put(`/${task.idprocess}/priorities/${newPriority.id}`, {});
      setSelectedPriority(newPriority);
      onPriorityUpdate && onPriorityUpdate(task.idprocesstoken, newPriority);
      sendSnack({ type: "success", message: data.responsemessage });
    } catch {
      // ignore
    }
  };

  const handlePriorityChange = (event: SelectChangeEvent<string | number>) => {
    const newPriority = allPriorities.find((priority) => priority.id === Number(event.target.value));
    if (newPriority) {
      updatePriority(newPriority);
    }
  };

  // userdata parsing
  useEffect(() => {
    const parsedUserData = getUserData(task?.userdata);
    const userDataEntries = Object.fromEntries(
      Object.entries(parsedUserData).map(([key, item]) => [key, item])
    );
    setUserDataEntries(userDataEntries);
    Object.entries(parsedUserData).forEach(([key, value]) => {
      if ((value as UserData).inCard) {
        localStorage.setItem(`userdata-per-${task?.idprocess}`, key);
      }
    });
  }, [task]);

  useEffect(() => {
    if (open) {
      getComments();
      getAttachments();
      getHistory();
      if (task?.idprocess) {
        processesApi.get(`/${task.idprocess}`).then(({ data }) => {
          const detail = data?.process?.descriptiondetail || data?.process?.description_detail || data?.descriptiondetail || data?.description_detail || null;
          if (detail) setDescriptionDetail(detail);
        }).catch(() => { });
      }
    }
    if (attachments.length <= 10) {
      setMaxAttachmentsError(false);
    }
  }, [getComments, getAttachments, open, attachments.length, task?.idprocess]);

  // Capture Ctrl+V globally so the user can paste a file from
  // anywhere in the dialog (not only when an input has focus).
  // The browser does not generate a `paste` event for non-editable
  // targets, so we intercept the keydown, focus a hidden input and
  // call `execCommand("paste")` to synthesise a paste event on
  // that input. The event then bubbles naturally to `document`,
  // where the existing DropZone listener picks it up and runs the
  // upload with the proper date+time filename.
  useEffect(() => {
    if (!open || !doing || readonly) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isPaste = (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "v";
      if (!isPaste) return;
      const target = e.target as HTMLElement | null;
      // If the user is already focused on an editable surface, let
      // the browser handle the paste natively (text in description
      // editor, comment field, etc.).
      if (target?.matches?.("input, textarea, [contenteditable='true'], [contenteditable='']")) {
        return;
      }
      e.preventDefault();
      const el = pasteCaptureRef.current;
      if (!el) return;
      el.focus();
      // Tiny delay so the browser has time to register the focus
      // before execCommand synthesises the paste event. Without
      // this the very first Ctrl+V (when the input was not yet
      // focused) is silently dropped on some browsers.
      setTimeout(() => {
        try {
          document.execCommand("paste");
        } catch {
          /* ignore */
        }
      }, 0);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, doing, readonly]);

  // RENDER
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={false}
        fullWidth
        maxWidth={isMobile ? false : "lg"}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            // Bottom-sheet style on mobile (rounded top corners + max height),
            // centered card on desktop.
            borderRadius: isMobile ? "24px 24px 0 0" : 2,
            // On iOS we keep the dialog *inside* the safe area instead
            // of letting fullScreen=true force the Paper under the
            // status bar / home indicator.
            //
            // - dvh is the dynamic viewport height (Safari iOS hides
            //   the address bar as the user scrolls, vh does not, dvh
            //   does) — so the Paper tracks the visible viewport
            //   instead of the inflated "100vh" that includes the
            //   address bar / home indicator.
            // - Subtracting env(safe-area-inset-*) makes the Paper
            //   stop at the status bar and at the home indicator.
            // - marginTop / marginBottom push the Paper *out* of the
            //   unsafe areas so its rounded corners and the dialog
            //   content stay visible and tappable.
            ...(isMobile && {
              height:
                "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
              maxHeight:
                "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
              marginTop: "env(safe-area-inset-top, 0px)",
              marginBottom: "env(safe-area-inset-bottom, 0px)",
              // Fill the viewport horizontally on mobile. MUI's
              // default wrapper applies a 32px margin to the Paper
              // and a max-width from `maxWidth` prop; we override
              // both so the dialog stretches edge-to-edge while
              // still respecting the safe area vertically.
              width: "100vw",
              maxWidth: "100vw",
              marginLeft: 0,
              marginRight: 0,
            }),
            ...(!isMobile && {
              height: "90vh",
            }),
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ bgcolor: "background.default", width: "100%", display: "flex", flexDirection: "column" }}>
          {/* Bottom-sheet drag handle (mobile only) */}
          {isMobile && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                pt: 1,
                pb: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "grey.700" : "grey.300",
                }}
              />
            </Box>
          )}

          {(!isMobile) && (
            <DialogHeader onClose={onClose}>
              <Box sx={{ px: 2, width: "100%", }}>
                {task && (
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "stretch" : "center"}
                    spacing={isMobile ? 1 : 2}
                  >
                    <Box flex={1} minWidth={0}>
                      {/* Title line: processmodelname (historyMode) or flownodename (normal) */}
                      {historyMode ? (
                        editingProcessName ? (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <TextField
                              value={editedProcessName}
                              onChange={(e) => setEditedProcessName(e.target.value)}
                              variant="standard"
                              size="small"
                              autoFocus
                              sx={{ flex: 1 }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveProcessName();
                                if (e.key === "Escape") cancelEditProcessName();
                              }}
                              disabled={saving}
                            />
                            <IconButton size="small" onClick={saveProcessName} disabled={saving}>
                              <Check fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditProcessName} disabled={saving}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Box
                            onClick={canEditProcessDetails ? startEditProcessName : undefined}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                              cursor: canEditProcessDetails ? 'pointer' : 'default',
                              '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                              transition: 'border-color 0.15s, background-color 0.15s',
                              display: 'inline-block',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                              {task.processmodelname}
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Typography
                          variant="h6"
                          color="text.primary"
                          sx={{ display: "block", fontWeight: 600 }}
                        >
                          {task.flownodename || task.name}
                        </Typography>
                      )}
                      {/* Subtitle line: description (historyMode) or processmodelname (normal) */}
                      {historyMode ? (
                        editingDescription ? (
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                            <TextField
                              value={editedDescription}
                              onChange={(e) => setEditedDescription(e.target.value)}
                              variant="standard"
                              size="small"
                              autoFocus
                              sx={{ flex: 1 }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveDescription();
                                if (e.key === "Escape") cancelEditDescription();
                              }}
                              onBlur={() => {
                                if (editedDescription.trim() !== task?.description) saveDescription();
                                else setEditingDescription(false);
                              }}
                              disabled={saving}
                            />
                            <IconButton size="small" onClick={saveDescription} disabled={saving}>
                              <Check fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEditDescription} disabled={saving}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Box
                            onClick={canEditProcessDetails ? startEditDescription : undefined}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                              cursor: canEditProcessDetails ? 'pointer' : 'default',
                              '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                              transition: 'border-color 0.15s, background-color 0.15s',
                              display: 'inline-block',
                            }}
                          >
                            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700 }}>
                              {task.description}
                            </Typography>
                          </Box>
                        )
                      ) : editingProcessName ? (
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                          <TextField
                            value={editedProcessName}
                            onChange={(e) => setEditedProcessName(e.target.value)}
                            variant="standard"
                            size="small"
                            autoFocus
                            sx={{ flex: 1 }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveProcessName();
                              if (e.key === "Escape") cancelEditProcessName();
                            }}
                            onBlur={() => {
                              if (editedProcessName.trim() && editedProcessName.trim() !== task?.processmodelname) saveProcessName();
                              else setEditingProcessName(false);
                            }}
                            disabled={saving}
                          />
                          <IconButton size="small" onClick={saveProcessName} disabled={saving}>
                            <Check fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditProcessName} disabled={saving}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Box
                          onClick={canEditProcessDetails ? startEditProcessName : undefined}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1,
                            py: 0.25,
                            cursor: canEditProcessDetails ? 'pointer' : 'default',
                            '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                            transition: 'border-color 0.15s, background-color 0.15s',
                            display: 'inline-block',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                            {task.processmodelname}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        maxWidth: "100%",
                        overflowX: isMobile ? "auto" : "visible",
                        overflowY: "hidden",
                        flexWrap: "nowrap",
                        pb: isMobile ? 0.5 : 0,
                        "& > *": { flexShrink: 0 },
                        "& .MuiButton-root": { whiteSpace: "nowrap" },
                      }}
                    >
                      <FlowentiButton
                        id="btn-download-task-detail-document"
                        size="small"
                        variant="outlined"
                        startIcon={<SaveAlt />}
                        disabled={downloadingDocument}
                        onClick={handleDownloadTaskDocument}
                        sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                      >
                        Download
                      </FlowentiButton>
                      <FlowentiButton
                        id="btn-preview-task-detail-document"
                        size="small"
                        variant="outlined"
                        startIcon={<Print />}
                        disabled={downloadingDocument}
                        onClick={handlePreviewTaskDocument}
                        sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                      >
                        {getTranslation(translations, "generic.print")}
                      </FlowentiButton>
                      <FlowentiButton
                        id="btn-share-task-link"
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon />}
                        disabled={!task}
                        onClick={() => setShareLinkOpen(true)}
                        sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                      >
                        {getTranslation(translations, "generic.share")}
                      </FlowentiButton>
                      {isMobile && (
                        <FlowentiButton
                          id="btn-share-task-detail-document"
                          size="small"
                          variant="outlined"
                          startIcon={<Share />}
                          disabled={downloadingDocument}
                          onClick={handleShareTaskDocument}
                          sx={{ py: 0.75, px: 1.5, whiteSpace: "nowrap" }}
                        >
                          {getTranslation(translations, "generic.share") || "Share"}
                        </FlowentiButton>
                      )}
                      <Labels labelCompleteMode labels={getLabelDataFromJsonTags(task?.jsontag)} />
                    </Stack>
                  </Stack>
                )}
              </Box>
            </DialogHeader>
          )}

          {/* Action buttons bar (desktop only — mobile uses MobileDetailToolbar) */}
          {!isMobile && (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: "background.paper",
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  maxWidth: "100%",
                  overflowY: "hidden",
                  pb: 0,
                  "& > *": { flexShrink: 0 },
                  "& .MuiButton-root": { whiteSpace: "nowrap" },
                }}
              >
                {/* Priority */}
                {selectedPriority && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {getTranslation(translations, "generic.priorities.caption")}:
                    </Typography>
                    {doing && !readonly ? (
                      <Select
                        value={selectedPriority.id}
                        onChange={handlePriorityChange}
                        displayEmpty
                        size="small"
                        sx={{
                          minWidth: 100,
                          height: 32,
                          "& .MuiSelect-select": { py: 0.5, px: 1 },
                        }}
                      >
                        {allPriorities.map((priority) => (
                          <MenuItem key={priority.id} value={priority.id}>
                            {priority.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        label={selectedPriority.name}
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                )}

                {/* Action buttons - visible only when doing and not readonly */}
                {!readonly && doing && (
                  <>

                    <FlowentiButton
                      startIcon={<LabelOutlined color="primary" />}
                      size="small"
                      variant="outlined"
                      onClick={() => setIsLabelManagerShown(true)}
                      sx={{ height: 32 }}
                    >
                      {getTranslation(translations, "generic.labels.caption")}
                    </FlowentiButton>
                    {checkLoading ? (
                      /* Lo spinner e' piu' stretto e piu' basso del pulsante
                         che sostituisce: quando il controllo dei permessi
                         finisce, la barra si riassesta di lato e — con
                         flexWrap — puo' andare a capo. Si riserva l'ingombro
                         finale del pulsante (altezza 32, la stessa che gli
                         altri pulsanti della riga si danno a mano). */
                      <Skeleton
                        variant="rounded"
                        height={32}
                        width={150}
                        data-testid="cf-action-buttons-skeleton"
                        role="status"
                        aria-busy="true"
                      />
                    ) : (
                      <>
                        <FlowentiButton
                          id="basic-button"
                          aria-controls={menuOpen ? "basic-menu" : undefined}
                          aria-haspopup="true"
                          aria-expanded={menuOpen ? "true" : undefined}
                          onClick={handleClick}
                          size="small"
                          variant="outlined"
                          startIcon={<AddToDrive color="primary" />}
                        >
                          {getTranslation(translations, "pm.startprocess.button.uploadfile.drive")}
                        </FlowentiButton>

                        {/* Bottone "aggiungi userdata" commentato: consentiva un
                            valore extra valido solo per questa istanza di
                            processo. Rimosso per semplificare l'UI — era una
                            seconda strada verso lo stesso scopo dello schema
                            del modello. La meccanica ad-hoc sotto resta. */}
                        {/* 
                        <FlowentiButton
                          id="addfield-button"
                          startIcon={<AddCircleOutlineRounded color="primary" />}
                          size="small"
                          variant="outlined"
                          onClick={() => setOpenAddFieldDialog(true)}
                        >
                          {getTranslation(translations, "generic.user_data.caption")}
                        </FlowentiButton>
                        */}
                        <FlowentiButton
                          id="create-album-button"
                          startIcon={<PhotoLibrary color="primary" />}
                          size="small"
                          variant="outlined"
                          onClick={() => setAlbumEditor({ open: true, album: null })}
                          disabled={albumCandidates.length === 0}
                        >
                          {getTranslation(translations, "album.new.title")}
                        </FlowentiButton>


                      </>
                    )}
                  </>
                )}
                <ProcessNavigationMenu
                  task={task}
                  handleNextStep={handleNextStep && ((nextTask) => afterFieldsSaved(() => handleNextStep(nextTask)))}
                  handleBackStep={handleBackStep && ((nextTask) => afterFieldsSaved(() => handleBackStep(nextTask)))}
                  handleSequenceFlowChoice={handleSequenceFlowChoice && ((nextTask, flow) => afterFieldsSaved(() => handleSequenceFlowChoice(nextTask, flow)))}
                  nextButtonLabel={nextButtonLabel}
                  undoButtonLabel={undoButtonLabel}
                  onClose={onClose}
                />

                <Box sx={{ flex: 1 }} />

                {/* History switch - always visible */}

                <ToggleButtonGroup
                  value={activeView}
                  exclusive
                  onChange={(event, newView) => {
                    if (newView !== null) {
                      setActiveView(newView);
                      setShowHistory(newView === 'histories');
                    }
                  }}
                  size="small"
                  aria-label="view selection"
                  sx={{
                    "& .MuiToggleButton-root": {
                      color: "primary.main",
                      borderColor: "divider",
                      "&:hover": {
                        color: "primary.light",
                        bgcolor: "action.hover",
                      },
                      "&.Mui-selected": {
                        color: "primary.main",
                        bgcolor: "action.selected",
                      },
                      "&.Mui-selected:hover": {
                        bgcolor: "action.hover",
                      },
                    },
                  }}
                >
                  <Tooltip title={getTranslation(translations, "settings.comments.label")}>
                    <ToggleButton
                      value="comments"
                      aria-label="comments"
                    >
                      <Comment color="inherit" />
                    </ToggleButton>

                  </Tooltip>
                  <Tooltip title={getTranslation(translations, "settings.histories.label")}>
                    <ToggleButton value="histories" aria-label="histories">
                      <History color="inherit" />
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </Stack>

              {/* Error alerts */}
              {!readonly && doing && (
                <>
                  {((storageDiskCanUse === false && !bypassSubscription) || uploadAttachmentError) && (
                    <Alert
                      sx={{ mt: 1, py: 0.5 }}
                      severity="error"
                      action={
                        <Button
                          size="small"
                          onClick={() => navigate(`/dashboard/organizations/${organizationId}/billing`, {
                            state: { organizationName: selectedOrganization?.name }
                          })}
                        >
                          {getTranslation(translations, "generic.upgrade")}
                        </Button>
                      }
                    >
                      {getTranslation(translations, "storage.limit.reached")}
                    </Alert>
                  )}

                  {maxAttachmentsError && (
                    <Alert
                      id="max-attachments-error"
                      sx={{ mt: 1, py: 0.5 }}
                      severity="error"
                    >
                      {getTranslation(translations, "attachment.limit.reached")}
                    </Alert>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Main content area */}
        <DialogContent
          sx={{
            p: 3,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: { xs: "auto", md: "hidden" },
            ...(isMobile && {
              p: 0,
              gap: 0,
            }),
          }}
        >
          {/* SLA della riga aperta: originale, vigente, override ed escalation
              (piano F3c sez. 5). Senza SLA o fuori dalla pagina attivita' la
              sezione non rende nulla e il dialog resta quello di prima. */}
          <SlaTaskSlaDetail task={task} administrator={!!administrator} />
          {isMobile ? (
            !task ? null : (
              <MobileTaskDetailDrawer
                fieldsRef={fieldsRef}
                task={task}
                onClose={onClose}
                onTakeInCarico={(t) => afterFieldsSaved(() => handleNextStep?.(t))}
                onUndo={(t) => afterFieldsSaved(() => handleBackStep?.(t))}
                onSequenceFlowChoice={(t, flow) => afterFieldsSaved(() => handleSequenceFlowChoice?.(t, flow))}
                onPriorityChange={async (t, p) => {
                  // Mirror the desktop updatePriority flow scoped to the current task.
                  try {
                    const { data } = await flownodesApi.put(`/${t.idprocess}/priorities/${p.id}`, {});
                    setSelectedPriority(p);
                    onPriorityUpdate && onPriorityUpdate(t.idprocesstoken, p);
                    sendSnack({ type: "success", message: data.responsemessage });
                  } catch {
                    // ignore
                  }
                }}
                administrator={!!administrator}
                historyMode={historyMode}
                doing={!!doing}
                readonly={!!readonly}
                translations={translations}
                onDownload={handleDownloadTaskDocument}
                onPreview={handlePreviewTaskDocument}
                onShare={handleShareTaskDocument}
                onShareLink={() => setShareLinkOpen(true)}
                downloadingDocument={downloadingDocument}
                comments={comments}
                history={history}
                onComment={async (text: string) => onComment({ comment: text })}
                onEditComment={onEditComment}
                attachments={surroundingAttachments}
                fieldAttachments={fieldAttachments}
                images={images}
                attachmentHistories={histories}
                getAttachments={getAttachments}
                handleUploadFile={async (files: File[]) => {
                  if (files && files.length > 0) await handleUploadFile(files as File[]);
                }}
                userdataEntries={userdataEntries}
                setUserDataEntries={setUserDataEntries}
                handleSaveUserdata={saveUserdata}
                refresh={refresh}
                sendSnack={(p) => sendSnack(p as any)}
                descriptionDetail={descriptionDetail}
                descriptionDetailReadOnly={descriptionDetailReadOnly}
                descriptionDetailRef={descriptionDetailRef}
                isDescriptionDetailFocused={isDescriptionDetailFocused}
                setIsDescriptionDetailFocused={setIsDescriptionDetailFocused}
                saveDescriptionDetail={saveDescriptionDetail}
                setShowEditClosedConfirm={setShowEditClosedConfirm}
                priorities={allPriorities}
                selectedPriority={selectedPriority}
                driveAnchorEl={anchorEl}
                onDriveOpen={(e) => handleClick(e as React.MouseEvent<HTMLButtonElement>)}
                onDriveClose={handleMenuClose}
                isLabelManagerOpen={isLabelManagerShown}
                onLabelManagerOpen={() => setIsLabelManagerShown(true)}
                onLabelManagerClose={handleLabelManagerClose}
              />
            )
          ) : (
            <PanelGroup
              orientation="horizontal"
              style={{ flex: 1, overflow: "hidden", height: "100%" }}
              defaultLayout={loadLayout(LAYOUT_KEY)}
              onLayoutChanged={(layout) => saveLayout(LAYOUT_KEY, layout)}
            >
              {/* LEFT panel: description, fields, attachments */}
              <Panel
                id="taskdetail-left"
                defaultSize="60%"
                minSize="35%"
                style={{ display: "flex", flexDirection: "column", overflow: "auto", padding: "0 12px 0 24px" }}
              >
                <Stack spacing={3}>
                  {/* Process name */}
                  {(task?.description || canEditProcessDetails) && (
                    <Box>
                      <Typography variant="h6" mb={2}>
                        {getTranslation(translations, "pm.processstart.processname.label") || "Nome processo"}
                      </Typography>
                      {!historyMode && task?.description && !editingDescription ? (
                        <Box
                          onClick={canEditProcessDetails ? startEditDescription : undefined}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            cursor: canEditProcessDetails ? 'pointer' : 'default',
                            '&:hover': canEditProcessDetails ? { borderColor: 'primary.main', bgcolor: 'action.hover' } : {},
                            transition: 'border-color 0.15s, background-color 0.15s',
                          }}
                        >
                          <Typography variant="body1">{task.description}</Typography>
                        </Box>
                      ) : !historyMode && editingDescription ? (
                        <TextField
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          variant="outlined"
                          size="small"
                          autoFocus
                          fullWidth
                          InputProps={{ sx: { borderRadius: 5 } }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveDescription();
                            if (e.key === "Escape") cancelEditDescription();
                          }}
                          onBlur={() => {
                            if (editedDescription.trim() !== task?.description) saveDescription();
                            else setEditingDescription(false);
                          }}
                          disabled={saving}
                        />
                      ) : historyMode && task?.description ? (
                        <Typography variant="body1">
                          {task.description}
                        </Typography>
                      ) : null}
                    </Box>
                  )}

                  {/* Rich text description */}
                  {(descriptionDetail || canEditProcessDetails) && (
                    <Box
                      onMouseDownCapture={(e) => {
                        if (isHistoryReadOnlyGate) {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowEditClosedConfirm(true);
                        }
                      }}
                      onFocusCapture={() => {
                        if (isHistoryReadOnlyGate) {
                          setShowEditClosedConfirm(true);
                        }
                      }}
                    >
                      <Typography variant="h6" mb={2}>
                        {getTranslation(translations, "pm.startprocess.description") || "Descrizione dettagliata"}
                      </Typography>
                      <RichTextEditor
                        ref={descriptionDetailRef}
                        value={descriptionDetail || ""}
                        readOnly={descriptionDetailReadOnly}
                        minHeight={isDescriptionDetailFocused ? 180 : 60}
                        onFocus={() => setIsDescriptionDetailFocused(true)}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                            setIsDescriptionDetailFocused(false);
                            saveDescriptionDetail();
                          }
                        }}
                        sx={{
                          width: "100%",
                          "& [class*='_root_']": { fontSize: "0.9375rem" },
                          "& [contenteditable]": {
                            fontSize: "0.9375rem",
                            minHeight: isDescriptionDetailFocused ? 180 : 60,
                          },
                          "& [contenteditable] h1": { fontSize: "1.4rem" },
                          "& [contenteditable] h2": { fontSize: "1.2rem" },
                          "& [contenteditable] h3": { fontSize: "1.05rem" },
                          "& [contenteditable] table": { fontSize: "0.9375rem" },
                          "& .cm-editor": { fontSize: "0.9375rem" },
                        }}
                      />
                    </Box>
                  )}

                  {/* Fields - scrollable */}
                  <Paper
                    sx={{
                      bgcolor: "background.paper",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "auto",
                    }}
                    elevation={0}
                  >
                    {Object.keys(userdataEntries).length !== 0 ? (
                      <Accordion
                        elevation={0}
                        disableGutters
                        defaultExpanded
                        sx={{
                          '&:before': {
                            display: 'none',
                          },
                          boxShadow: 'none',
                          '&.MuiAccordion-root': {
                            border: 'none',
                          },
                          '&.Mui-expanded': {
                            margin: 0,
                          },
                        }}
                      >
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                              {getTranslation(translations, "generic.user_data.caption")}
                            </Typography>
                            <UserDataFieldsInfo stopPropagation />
                          </Stack>
                        </AccordionSummary>

                        <AccordionDetails>
                          <FieldsList
                            ref={fieldsRef}
                            processId={task?.idprocess == null ? undefined : String(task.idprocess)}
                            taskId={task?.idprocesstoken == null ? undefined : String(task.idprocesstoken)}
                            canManage={doing && !readonly}
                            schema={task?.userschema}
                            selectedProcess={task?.idprocess}
                            modelId={task?.idprocessmodel == null ? undefined : String(task.idprocessmodel)}
                            value={userdataEntries}
                            onChange={setUserDataEntries}
                            onRefresh={refresh}
                            openAddFieldDialog={openAddFieldDialog}
                            onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                            attachments={fieldAttachments}
                            handleSaveUserdata={async (payload, _silent, operationId, removedFieldIds, context) => {
                              await saveUserdata(payload, operationId, removedFieldIds, context);
                            }}
                            readonly={task?.state === 0}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        <FieldsList
                          ref={fieldsRef}
                          processId={task?.idprocess == null ? undefined : String(task.idprocess)}
                          taskId={task?.idprocesstoken == null ? undefined : String(task.idprocesstoken)}
                          canManage={doing && !readonly}
                          schema={task?.userschema}
                          selectedProcess={task?.idprocess}
                          modelId={task?.idprocessmodel == null ? undefined : String(task.idprocessmodel)}
                          value={userdataEntries}
                          onChange={setUserDataEntries}
                          onRefresh={refresh}
                          openAddFieldDialog={openAddFieldDialog}
                          onAddFieldDialogClose={() => setOpenAddFieldDialog(false)}
                          attachments={fieldAttachments}
                          handleSaveUserdata={async (payload, _silent, operationId, removedFieldIds, context) => {
                            await saveUserdata(payload, operationId, removedFieldIds, context);
                          }}
                          readonly={task?.state === 0}
                        />
                      </Box>
                    )}
                    {/* Attachments accordion - shown when there are attachments OR when user can add */}
                    {(surroundingAttachments.length > 0 || (doing && !readonly)) && (
                      <Accordion
                        elevation={0}
                        disableGutters
                        defaultExpanded
                        sx={{
                          '&:before': {
                            display: 'none',
                          },
                          boxShadow: 'none',
                          '&.MuiAccordion-root': {
                            border: 'none',
                          },
                          '&.Mui-expanded': {
                            margin: 0,
                          },
                        }}

                      >
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                            {getTranslation(translations, "settings.attachments.label")} ({surroundingAttachments.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{ mt: -3 }}
                        >
                          {uploading && (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                              <CircularProgress size={18} />
                              <Typography variant="body2" color="text.secondary">
                                {getTranslation(translations, "generic.loading") || "Caricamento..."}
                              </Typography>
                            </Stack>
                          )}
                          {doing && !readonly && (
                            <DropZone
                              files={[]}
                              externalFilesCount={attachments.length}
                              maxFiles={10 - attachments.length}
                              subscriptionFeatures={{
                                bypassSubscription,
                                storageDiskCanUse,
                                refreshSubscriptionFeatures,
                                storageUsage,
                                checkLoading,
                              }}
                              onFileChange={async (files) => {
                                if (files.length > 0) {
                                  await handleUploadFile(files);
                                }
                              }}
                            />
                          )}
                          <AttachmentList
                            canManage={doing && !readonly}
                            attachments={surroundingAttachments}
                            knownWidth={0}
                            getAttachments={getAttachments}
                            images={images}
                            histories={histories}
                          />
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {/* Album accordion — SOLO se esiste almeno un album (il bottone
                        "Nuovo album" sta nella barra azioni, di fianco a "dati utente") */}
                    {albums.length > 0 && (
                      <Accordion
                        elevation={0}
                        disableGutters
                        defaultExpanded
                        sx={{
                          '&:before': { display: 'none' },
                          boxShadow: 'none',
                          '&.MuiAccordion-root': { border: 'none' },
                          '&.Mui-expanded': { margin: 0 },
                        }}
                      >
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                            {getTranslation(translations, "album.label")} ({albums.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ mt: -2 }}>
                          <Stack spacing={3}>
                            {albums.map((album) => (
                              <Box key={album.id}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                  <Typography variant="subtitle2">{album.name}</Typography>
                                  <Box sx={{ flex: 1 }} />
                                  {doing && !readonly && (
                                    <Button
                                      size="small"
                                      onClick={() => setAlbumEditor({ open: true, album })}
                                    >
                                      {getTranslation(translations, "pm.button.edit")}
                                    </Button>
                                  )}
                                </Stack>
                                <AlbumGallery
                                  items={album.images}
                                  thumbFor={(id) => images[id]}
                                  labelFor={(id) =>
                                    attachments.find((a) => a.id === id)?.filename ?? ""
                                  }
                                />
                              </Box>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {/* Hidden input that receives the focus from the
                        global Ctrl+V listener above. The synthesized
                        paste event bubbles up to `document` where the
                        DropZone listener handles it. No `onPaste`
                        handler is wired here on purpose — we want the
                        event to bubble untouched. */}
                    {doing && !readonly && (
                      <input
                        ref={pasteCaptureRef}
                        id="task-detail-paste-capture"
                        aria-hidden="true"
                        tabIndex={-1}
                        autoFocus
                        style={{
                          position: "absolute",
                          width: 1,
                          height: 1,
                          opacity: 0,
                          pointerEvents: "none",
                          left: -1000,
                          top: -1000,
                        }}
                      />
                    )}
                  </Paper>
                </Stack>
              </Panel>

              <PanelResizeHandle className="panel-resize-handle" style={{
                width: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "col-resize",
                transition: "background 0.15s",
              }}>
                <Box
                  sx={{
                    width: 6,
                    height: 38,
                    borderRadius: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "#ffffff" : "action.hover",
                    py: 0.75,
                    transition: "background-color 0.15s",
                  }}
                >
                  {[0, 1, 2].map((dot) => (
                    <Box
                      key={dot}
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "#000000" : "text.disabled",
                      }}
                    />
                  ))}
                </Box>
              </PanelResizeHandle>

              {/* RIGHT panel: Comments / History */}
              <Panel
                id="taskdetail-right"
                defaultSize="40%"
                minSize="25%"
                style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                <Paper
                  sx={{
                    width: "100%",
                    bgcolor: "background.default",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    height: "100%",
                  }}
                  elevation={0}
                >
                  <Box sx={{ p: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 18 }}>
                      {!showHistory
                        ? getTranslation(translations, "settings.comments.label")
                        : getTranslation(translations, "settings.histories.label")
                      }
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", p: 2 }}>
                    {!showHistory ? (
                      <CommentsSection
                        canWrite={doing && !readonly}
                        canEdit={doing && !readonly}
                        onComment={onComment}
                        onEdit={onEditComment}
                        comments={comments}
                        mentionOrgId={organizationId ?? undefined}
                      />
                    ) : (
                      <HistoriesSection
                        history={history}
                      />
                    )}
                  </Box>
                </Paper>
              </Panel>
            </PanelGroup>
          )}
        </DialogContent>

        {/* Google Drive Menu */}
        <GoogleDriveMenu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onRefreshAttachments={getAttachments}
          task={task}
          organizationId={organizationId}
          userLocale={user?.i18n}
        />

        <LabelManager
          idprocess={task?.idprocess}
          open={isLabelManagerShown}
          handleClose={handleLabelManagerClose}
        />
        <ConfirmDialog
          open={fullContentDialog.open}
          onClose={closeFullContentDialog}
          onConfirm={closeFullContentDialog}
          //  cancelText={getTranslation(translations, "generic.button.cancel")}
          confirmText={getTranslation(translations, "pm.minimap.close.lable")}
          title={task?.description || fullContentDialog.title}
          maxWidth="lg"
          fullWidth={true}
          content={
            <Box sx={{ whiteSpace: "pre-wrap", maxHeight: "600px", minHeight: "300px" }}>
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(fullContentDialog.content) }} />
            </Box>
          }
        />
        <Dialog
          open={showEditClosedConfirm}
          onClose={closeEditClosedConfirm}
          fullWidth
          maxWidth="xs"
        >
          <DialogHeader onClose={closeEditClosedConfirm}>
            {getTranslation(translations, "pm.confirm.edit_closed_process_title")
              || "Modifica processo chiuso"}
          </DialogHeader>
          <DialogContent sx={{ pt: 3 }}>
            {getTranslation(translations, "pm.confirm.edit_closed_process_body")
              || "Sei sicuro di voler modificare un processo già chiuso?"}
          </DialogContent>
          <DialogActions>
            <FlowentiButton
              id="edit-closed-cancel-btn"
              size="small"
              variant="outlined"
              onClick={closeEditClosedConfirm}
            >
              {getTranslation(translations, "generic.button.cancel")}
            </FlowentiButton>
            <FlowentiButton
              id="edit-closed-confirm-btn"
              size="small"
              variant="contained"
              onClick={confirmEditClosed}
            >
              {getTranslation(translations, "generic.button.confirm")}
            </FlowentiButton>
          </DialogActions>
        </Dialog>
      </Dialog >

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            height: isMobile ? "100%" : "85vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogHeader onClose={closePreview}>
          <Typography variant="h6" sx={{ px: 2 }}>
            {task?.flownodename || task?.name || "Preview"}
          </Typography>
        </DialogHeader>
        <DialogContent sx={{ p: 0, flex: 1, overflow: isMobile ? "auto" : "hidden" }}>
          {isMobile ? renderMobileTaskDocumentPreview() : previewPdfUrl && (
            <iframe
              src={previewPdfUrl}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>

      <TaskShareLinkDialog
        open={shareLinkOpen}
        onClose={() => setShareLinkOpen(false)}
        task={task}
        attachments={attachments}
        comments={comments}
        history={history}
        albums={albums}
      />

      <AlbumEditDialog
        open={albumEditor.open}
        onClose={() => setAlbumEditor({ open: false, album: null })}
        idprocess={task?.idprocess ?? 0}
        album={albumEditor.album}
        candidates={albumCandidates}
        onSaved={getAlbums}
      />
    </>
  );
};

export default TaskDetailDialog;
